// The live connection (WebSocket at /api/v1/ws) for the open organization.
//
// Events from the server are applied straight to the query cache (new messages, edits, reactions, unread counts,
// presence), so screens just read their queries. After a reconnect everything is fetched again, since events may have
// been missed meanwhile. Typing indicators live in a small store of their own (they change many times a second).
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import { keys, type ConversationSummary, type Message, type MessagePage, type Presence } from "./chat";

type Status = "connecting" | "open" | "closed";
type Typist = { id: string; name: string; until: number };

class TypingStore {
  private byConversation = new Map<string, Typist[]>();
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  subscribe = (fn: () => void) => { this.listeners.add(fn); return () => this.listeners.delete(fn); };
  get = (conversationId: string) => this.byConversation.get(conversationId) ?? EMPTY;
  add(conversationId: string, person: { id: string; name: string }) {
    const others = this.get(conversationId).filter((t) => t.id !== person.id);
    this.byConversation.set(conversationId, [...others, { ...person, until: Date.now() + 5000 }]);
    this.emit();
    this.timer ??= setInterval(() => this.expire(), 1000);
  }
  remove(conversationId: string, userId: string) {
    const list = this.get(conversationId);
    if (!list.some((t) => t.id === userId)) return;
    this.byConversation.set(conversationId, list.filter((t) => t.id !== userId));
    this.emit();
  }
  private expire() {
    const now = Date.now();
    let changed = false;
    for (const [id, list] of this.byConversation) {
      const kept = list.filter((t) => t.until > now);
      if (kept.length !== list.length) { changed = true; if (kept.length) this.byConversation.set(id, kept); else this.byConversation.delete(id); }
    }
    if (!this.byConversation.size && this.timer) { clearInterval(this.timer); this.timer = null; }
    if (changed) this.emit();
  }
  private emit() { for (const fn of this.listeners) fn(); }
}
const EMPTY: Typist[] = [];

type LiveListener = (event: Record<string, unknown>) => void;

// One screen throwing on an event must not stop the others hearing it.
function notify(listener: LiveListener, event: Record<string, unknown>) {
  try { listener(event); } catch (err) { console.error("live listener", err); }
}

type Live = {
  status: Status;
  // Screens that react to events themselves (incoming calls, the call screen). Returns the unsubscribe function.
  subscribe: (listener: LiveListener) => () => void;
  sendTyping: (conversationId: string) => void;
  // The conversation on screen: new messages there don't count as unread.
  setActiveConversation: (id: string | null) => void;
  // On the call screen: shown to colleagues as "In a call".
  setInCall: (inCall: boolean) => void;
  typing: TypingStore;
};

const LiveContext = createContext<Live | null>(null);

export function useLive(): Live {
  const live = useContext(LiveContext);
  if (!live) throw new Error("useLive outside LiveProvider");
  return live;
}

// Calls `handler` for every live event while the component is mounted (always the latest handler).
export function useLiveEvent(handler: LiveListener) {
  const { subscribe } = useLive();
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => subscribe((e) => latest.current(e)), [subscribe]);
}

export function useTyping(conversationId: string): Typist[] {
  const { typing } = useLive();
  return useSyncExternalStore(typing.subscribe, () => typing.get(conversationId));
}

// ---------------------------------------------------------------- cache updates

type Pages = InfiniteData<MessagePage, number | undefined>;

export function upsertMessage(list: Message[], m: Message): Message[] {
  const i = list.findIndex((x) => x.id === m.id || (m.clientId !== null && x.clientId === m.clientId));
  if (i === -1) return [...list, m];
  const copy = list.slice();
  copy[i] = { ...m, pending: undefined };
  return copy;
}

export function addToPages(qc: QueryClient, slug: string, conversationId: string, m: Message) {
  qc.setQueryData<Pages>(keys.messages(slug, conversationId), (old) => {
    if (!old?.pages.length) return old;
    const [newest, ...rest] = old.pages;
    return { ...old, pages: [{ ...newest!, messages: upsertMessage(newest!.messages, m) }, ...rest] };
  });
}

export function patchMessage(qc: QueryClient, slug: string, conversationId: string, id: string, patch: (m: Message) => Message) {
  qc.setQueryData<Pages>(keys.messages(slug, conversationId), (old) => old && {
    ...old, pages: old.pages.map((p) => ({ ...p, messages: p.messages.map((m) => (m.id === id ? patch(m) : m)) })),
  });
  qc.setQueriesData<{ root: Message; replies: Message[] }>({ queryKey: ["replies", slug, conversationId] }, (old) => old && {
    root: old.root.id === id ? patch(old.root) : old.root,
    replies: old.replies.map((m) => (m.id === id ? patch(m) : m)),
  });
}

function applyEvent(qc: QueryClient, slug: string, me: string, active: string | null, e: Record<string, unknown>, typing: TypingStore) {
  switch (e.type) {
    case "message.created": {
      const m = e.message as Message;
      const root = e.root as { id: string; replyCount: number; lastReplyAt: string | null } | null;
      typing.remove(m.conversationId, m.author?.id ?? "");
      if (m.parentId) {
        qc.setQueryData<{ root: Message; replies: Message[] }>(keys.replies(slug, m.conversationId, m.parentId), (old) => old && { ...old, replies: upsertMessage(old.replies, m) });
        if (root) patchMessage(qc, slug, m.conversationId, root.id, (r) => ({ ...r, replyCount: root.replyCount, lastReplyAt: root.lastReplyAt }));
        return;
      }
      addToPages(qc, slug, m.conversationId, m);
      const onScreen = active === m.conversationId && document.visibilityState === "visible";
      let known = false;
      qc.setQueryData<{ conversations: ConversationSummary[] }>(keys.conversations(slug), (old) => {
        if (!old) return old;
        const c = old.conversations.find((x) => x.id === m.conversationId);
        if (!c) return old;
        known = true;
        const updated: ConversationSummary = {
          ...c,
          unread: m.author?.id === me || onScreen ? c.unread : c.unread + 1,
          lastMessageAt: m.createdAt,
          lastMessage: { body: m.body.slice(0, 140), deleted: false, authorName: m.author?.name ?? "", createdAt: m.createdAt },
        };
        return { conversations: [updated, ...old.conversations.filter((x) => x.id !== c.id)] };
      });
      if (!known) qc.invalidateQueries({ queryKey: keys.conversations(slug) });
      return;
    }
    case "message.updated": {
      const m = e.message as Message;
      patchMessage(qc, slug, m.conversationId, m.id, () => m);
      return;
    }
    case "message.deleted":
      patchMessage(qc, slug, String(e.conversationId), String(e.messageId), (m) => ({ ...m, deleted: true, body: "", reactions: [], files: [], mentions: [] }));
      qc.invalidateQueries({ queryKey: keys.conversations(slug) });
      return;
    case "conversation.read":
      qc.setQueryData<{ conversations: ConversationSummary[] }>(keys.conversations(slug), (old) => old && {
        conversations: old.conversations.map((c) => (c.id === e.conversationId ? { ...c, unread: 0 } : c)),
      });
      qc.invalidateQueries({ queryKey: keys.notifications(slug) });   // reading a conversation reads its notifications
      return;
    case "conversation.added":
    case "conversation.updated":
    case "conversation.removed":
      qc.invalidateQueries({ queryKey: keys.conversations(slug) });
      qc.invalidateQueries({ queryKey: keys.conversation(slug, String(e.conversationId)) });
      return;
    case "teams.changed":
      qc.invalidateQueries({ queryKey: keys.teams(slug) });
      qc.invalidateQueries({ queryKey: ["team", slug] });
      qc.invalidateQueries({ queryKey: keys.conversations(slug) });
      return;
    case "notification.created":
      qc.invalidateQueries({ queryKey: keys.notifications(slug) });
      return;
    case "call.updated":
    case "call.ended":
      qc.invalidateQueries({ queryKey: ["call", slug, String(e.callId)] });
      qc.invalidateQueries({ queryKey: ["calls", slug] });
      return;
    case "join_requests.changed":
      qc.invalidateQueries({ queryKey: ["join-requests", slug] });
      return;
    case "members.changed":
      qc.invalidateQueries({ queryKey: ["members", slug] });
      qc.invalidateQueries({ queryKey: ["people", slug] });
      return;
    case "orgs.changed":
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["joinable"] });
      return;
    case "profile.updated":
      qc.invalidateQueries({ queryKey: ["people", slug] });
      if (e.userId === me) qc.invalidateQueries({ queryKey: ["me"] });
      return;
    case "presence":
      qc.setQueryData<{ presence: Record<string, Presence> }>(keys.presence(slug), (old) => ({ presence: { ...(old?.presence ?? {}), [String(e.userId)]: e.status as Presence } }));
      return;
    case "typing": {
      const user = e.user as { id: string; name: string };
      if (user.id !== me) typing.add(String(e.conversationId), user);
      return;
    }
  }
}

// ---------------------------------------------------------------- the connection

export function LiveProvider({ slug, me, children }: { slug: string; me: string; children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("connecting");
  const socket = useRef<WebSocket | null>(null);
  const active = useRef<string | null>(null);
  const lastTyping = useRef(new Map<string, number>());
  const typing = useMemo(() => new TypingStore(), []);
  const listeners = useRef(new Set<LiveListener>());
  const inCall = useRef(false);
  const statusSender = useRef<() => void>(() => {});

  useEffect(() => {
    let stopped = false;
    let attempt = 0;
    let everReady = false;
    let ping: ReturnType<typeof setInterval> | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const send = (msg: object) => { if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(msg)); };
    const sendStatus = () => send({ type: "status", status: inCall.current ? "busy" : document.visibilityState === "visible" ? "online" : "away" });
    statusSender.current = sendStatus;

    function connect() {
      clearTimeout(retry);
      setStatus("connecting");
      const ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/v1/ws`);
      socket.current = ws;
      ws.onopen = () => {
        ws.send(JSON.stringify({ type: "hello", org: slug }));
        ping = setInterval(() => send({ type: "ping" }), 25_000);
      };
      ws.onmessage = (ev) => {
        let e: Record<string, unknown>;
        try { e = JSON.parse(String(ev.data)); } catch { return; }
        if (e.type === "ready") {
          attempt = 0;
          setStatus("open");
          sendStatus();
          // Back after a gap: fetch everything again (events may have been missed).
          if (everReady) {
            for (const key of ["conversations", "messages", "replies", "notifications", "presence", "teams", "team"]) qc.invalidateQueries({ queryKey: [key, slug] });
          }
          everReady = true;
          for (const listener of listeners.current) notify(listener, e);
          return;
        }
        if (e.type === "error" && e.code === "NOT_A_MEMBER") { stopped = true; ws.close(); navigate("/", { replace: true }); return; }
        applyEvent(qc, slug, me, active.current, e, typing);
        for (const listener of listeners.current) notify(listener, e);
      };
      ws.onclose = (ev) => {
        clearInterval(ping);
        if (socket.current === ws) socket.current = null;
        setStatus("closed");
        if (stopped) return;
        if (ev.code === 4401) { qc.invalidateQueries({ queryKey: ["me"] }); return; }   // signed out: the shell goes to sign-in
        if (ev.code === 4403) { navigate("/", { replace: true }); return; }
        const delay = Math.min(30_000, 1000 * 2 ** attempt) * (0.5 + Math.random() / 2);
        attempt += 1;
        retry = setTimeout(connect, delay);
      };
    }

    const onVisibility = () => sendStatus();
    const onOnline = () => { if (!socket.current) connect(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    connect();
    return () => {
      stopped = true;
      clearInterval(ping);
      clearTimeout(retry);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      socket.current?.close(1000);
      socket.current = null;
    };
  }, [slug, me, qc, navigate, typing]);

  const value = useMemo<Live>(() => ({
    status,
    typing,
    subscribe: (listener) => { listeners.current.add(listener); return () => { listeners.current.delete(listener); }; },
    setActiveConversation: (id) => { active.current = id; },
    setInCall: (value) => { inCall.current = value; statusSender.current(); },
    sendTyping: (conversationId) => {
      const now = Date.now();
      if (now - (lastTyping.current.get(conversationId) ?? 0) < 3000) return;
      lastTyping.current.set(conversationId, now);
      if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify({ type: "typing", conversationId }));
    },
  }), [status, typing]);

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}
